import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  DeploymentArtifact,
  DeploymentStatus,
  EnvironmentView,
  ReleaseDeployment,
  ReleaseDetail,
  ReleaseModuleLine,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  StatusBadge,
  Textarea,
} from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { DEPLOYMENT_STATUS_LABELS, describeAttempt } from "./releaseVocabulary";

/** How a deployment can end. PLANNED and IN_PROGRESS are states, not outcomes. */
const OUTCOMES: DeploymentStatus[] = ["SUCCEEDED", "FAILED", "ROLLED_BACK", "CANCELLED"];

/**
 * Starting a deployment, or recording how one ended (§62).
 *
 * <p>The attempt number is not asked for. It is worked out on the server from what has already
 * been tried, because a caller who could choose it could reuse the number of the attempt that
 * failed — and a deployment history that can be overwritten answers "did this go cleanly?"
 * with whatever the last person typed.
 */
export function DeploymentDrawer({
  release,
  deployment,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  deployment: ReleaseDeployment | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const finishing = deployment != null;
  const [settling, setSettling] = useState<string | null>(null);

  /**
   * Lands or skips one module without ending the attempt.
   *
   * <p>Failing is not offered here: a failure has to say what went wrong, and a drawer that is
   * already asking how the night ended is the wrong place to ask a second question. The card
   * carries that one.
   */
  const settleHere = async (
    carried: DeploymentArtifact,
    status: "SUCCEEDED" | "SKIPPED",
  ) => {
    if (!deployment) {
      return;
    }
    setSettling(carried.id);
    try {
      await api.put(
        `/api/v1/releases/${release.id}/deployments/${deployment.id}/modules/${carried.id}`,
        { status, result: null, lockVersion: carried.lockVersion },
      );
      onSaved();
    } finally {
      setSettling(null);
    }
  };
  const [environmentId, setEnvironmentId] = useState("");
  const [artifactVersion, setArtifactVersion] = useState("");
  /* Which modules this attempt carries, and at what version. Pre-filled from the manifest —
     what the release intends to ship is the right opening answer for what is going out. */
  const [carrying, setCarrying] = useState<Record<string, string>>({});
  const [pipeline, setPipeline] = useState("");
  const [outcome, setOutcome] = useState<DeploymentStatus>("SUCCEEDED");
  const [result, setResult] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  const environments = useQuery({
    queryKey: ["release-environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open && !finishing,
  });

  /* The manifest: which modules this release moves, and to what. The opening answer for what a
     deployment is carrying. */
  const manifest = useQuery({
    queryKey: ["release-manifest", release.id],
    queryFn: () => api.get<ReleaseModuleLine[]>(`/api/v1/releases/${release.id}/modules`),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setEnvironmentId(release.targetEnvironment?.id ?? "");
    setArtifactVersion(release.artifactVersion ?? "");
    setCarrying({});
    setPipeline("");
    setOutcome("SUCCEEDED");
    setResult("");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deployment]);

  const needsReason = finishing && outcome === "FAILED";
  const canSubmit = finishing
    ? !needsReason || result.trim() !== ""
    : environmentId !== "";

  const submit = () =>
    void run(
      () =>
        finishing && deployment
          ? api.put(`/api/v1/releases/${release.id}/deployments/${deployment.id}`, {
              outcome,
              result: result.trim() === "" ? null : result.trim(),
              lockVersion: deployment.lockVersion,
            })
          : api.post(`/api/v1/releases/${release.id}/deployments`, {
              environmentId,
              artifactVersion: artifactVersion.trim() === "" ? null : artifactVersion.trim(),
              pipelineRunReference: pipeline.trim() === "" ? null : pipeline.trim(),
              modules: Object.entries(carrying)
                .filter(([, version]) => version.trim() !== "")
                .map(([moduleId, version]) => ({ moduleId, version: version.trim() })),
            }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={finishing ? "How did it end?" : "Start a deployment"}
      subtitle={
        finishing && deployment
          ? describeAttempt(deployment.attemptNo, deployment.environment.name)
          : "Recorded as a fresh attempt — a retry never overwrites the one before it."
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : finishing ? "Record outcome" : "Start"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {finishing ? (
          <>
            {/* The modules this attempt carried, settled here as well as on the card. The
                attempt cannot be called succeeded until each has landed, and being told that
                without being shown what is outstanding is the shape of a dead end. */}
            {deployment.artifacts.length > 0 && (
              <Field
                label="Modules in this attempt"
                hint="Each settles on its own. The attempt can only be recorded as succeeded once every one has landed or been skipped."
              >
                <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                  {deployment.artifacts.map((carried) => (
                    <li key={carried.id} className="flex items-center gap-2 px-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                        {carried.module.name}
                      </span>
                      <span className="tnum shrink-0 font-mono text-xs text-ink-600">
                        {carried.moduleVersion ?? "—"}
                      </span>
                      {carried.status === "IN_PROGRESS" ? (
                        <span className="flex shrink-0 gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={settling === carried.id}
                            onClick={() => void settleHere(carried, "SUCCEEDED")}
                          >
                            Landed
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={settling === carried.id}
                            onClick={() => void settleHere(carried, "SKIPPED")}
                          >
                            Skip
                          </Button>
                        </span>
                      ) : (
                        <StatusBadge
                          tone={carried.status === "SUCCEEDED" ? "green" : "amber"}
                        >
                          {carried.status === "SUCCEEDED" ? "Landed" : carried.status}
                        </StatusBadge>
                      )}
                    </li>
                  ))}
                </ul>
              </Field>
            )}

            <Field label="Outcome" required>
              <Select
                value={outcome}
                onChange={(event) => setOutcome(event.target.value as DeploymentStatus)}
              >
                {OUTCOMES.map((value) => (
                  <option key={value} value={value}>
                    {DEPLOYMENT_STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={needsReason ? "What went wrong?" : "Notes"}
              required={needsReason}
              hint={
                needsReason
                  ? "The first thing anybody reads afterwards. A blank here is where the question is."
                  : undefined
              }
            >
              <Textarea
                rows={3}
                value={result}
                onChange={(event) => setResult(event.target.value)}
                placeholder={
                  needsReason ? "Migration timed out on the orders table." : undefined
                }
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Environment" required>
              <Select
                value={environmentId}
                onChange={(event) => setEnvironmentId(event.target.value)}
              >
                <option value="">Choose an environment…</option>
                {(environments.data ?? []).map((environment) => (
                  <option key={environment.id} value={environment.id}>
                    {environment.name}
                    {environment.production ? " (production)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            {/* A product built from parts deploys the parts. One artifact string could not name
                four modules at once, and the value that ended up there named none of them. */}
            {(manifest.data ?? []).length > 0 && (
              <Field
                label="Modules going out"
                hint="Tick what this attempt carries. Each one is recorded separately, so three landing while a fourth fails is a thing you can write down."
              >
                <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                  {(manifest.data ?? []).map((line) => {
                    const chosen = carrying[line.module.id] != null;
                    return (
                      <div
                        key={line.module.id}
                        className="flex items-center gap-3 px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 shrink-0 rounded border-ink-300"
                          checked={chosen}
                          onChange={() =>
                            setCarrying((now) => {
                              const next = { ...now };
                              if (chosen) {
                                delete next[line.module.id];
                              } else {
                                next[line.module.id] = line.currentVersion ?? "";
                              }
                              return next;
                            })
                          }
                        />
                        <span className="min-w-0 flex-1 truncate text-sm text-ink-900">
                          {line.module.name}
                        </span>
                        {/* Wrapped rather than given a width class: the Input's own base styles
                            start with w-full, and cn() is a plain join — both classes land and
                            w-full wins, which squeezed the module name to nothing. */}
                        <div className="w-28 shrink-0">
                        <Input
                          disabled={!chosen}
                          value={carrying[line.module.id] ?? ""}
                          placeholder={line.currentVersion ?? "version"}
                          onChange={(event) =>
                            setCarrying((now) => ({
                              ...now,
                              [line.module.id]: event.target.value,
                            }))
                          }
                        />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Field>
            )}

            <Field label="Artifact version" hint="What is actually being deployed.">
              <Input
                value={artifactVersion}
                onChange={(event) => setArtifactVersion(event.target.value)}
                placeholder="1.0.0-build.42"
              />
            </Field>
            <Field label="Pipeline run" hint="A link or reference, so the log can be found later.">
              <Input
                value={pipeline}
                onChange={(event) => setPipeline(event.target.value)}
                placeholder="pipeline/1042"
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
